import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BirthdayDto,
  CalendarEventDto,
  ClassroomDto,
  ClassroomSettingsDto,
  PreferencesDto,
} from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import { BirthdayImport } from './BirthdayImport.js';
import styles from './settingsForm.module.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function CalendarPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;

  const settingsQuery = useQuery<ClassroomSettingsDto>({
    queryKey: ['classroom-settings', classroomId],
    queryFn: () => api.home.getSettings(classroomId),
  });

  const eventsQuery = useQuery<CalendarEventDto[]>({
    queryKey: ['calendar-events', classroomId],
    queryFn: () => api.calendar.listEvents(classroomId),
  });

  const birthdaysQuery = useQuery<BirthdayDto[]>({
    queryKey: ['calendar-birthdays', classroomId],
    queryFn: () => api.calendar.listBirthdays(classroomId),
  });

  const preferencesQuery = useQuery<PreferencesDto>({
    queryKey: ['preferences'],
    queryFn: () => api.preferences.get(),
  });

  const resetAll = useMutation({
    mutationFn: async () => {
      await api.home.patchSettings(classroomId, {
        schoolCalUrl: '',
        schoolCalTitle: '',
      });
      const events = await api.calendar.listEvents(classroomId);
      await Promise.all(events.map((ev) => api.calendar.removeEvent(classroomId, ev.id)));
      await api.calendar.clearBirthdays(classroomId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom-settings', classroomId] });
      void queryClient.invalidateQueries({ queryKey: ['calendar-events', classroomId] });
      void queryClient.invalidateQueries({ queryKey: ['calendar-birthdays', classroomId] });
    },
  });

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Calendar</h2>
          <p className={styles.description}>
            Class events, student birthdays, and the school calendar. Federal and &ldquo;fun&rdquo;
            holidays are added automatically.
          </p>
        </div>
        <ResetSettingsButton
          pageLabel="Calendar"
          onReset={() => resetAll.mutate()}
          disabled={resetAll.isPending}
        />
      </header>

      <div className={styles.twoCol}>
        <SchoolCalendarFields
          classroomId={classroomId}
          settings={settingsQuery.data}
          isLoading={settingsQuery.isLoading}
        />
        <YesterdayTomorrowToggle
          preferences={preferencesQuery.data}
          isLoading={preferencesQuery.isLoading}
        />
      </div>

      <div className={styles.twoCol}>
        <EventsSection
          classroomId={classroomId}
          events={eventsQuery.data ?? []}
          isLoading={eventsQuery.isLoading}
        />
        <BirthdaysSection
          classroomId={classroomId}
          birthdays={birthdaysQuery.data ?? []}
          isLoading={birthdaysQuery.isLoading}
        />
      </div>
    </div>
  );
}

function SchoolCalendarFields({
  classroomId,
  settings,
  isLoading,
}: {
  classroomId: string;
  settings: ClassroomSettingsDto | undefined;
  isLoading: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(settings?.schoolCalUrl ?? '');
  const [title, setTitle] = useState(settings?.schoolCalTitle ?? '');

  useEffect(() => {
    if (settings) {
      setUrl(settings.schoolCalUrl);
      setTitle(settings.schoolCalTitle);
    }
  }, [settings, classroomId]);

  const save = useMutation({
    mutationFn: (patch: { schoolCalUrl?: string; schoolCalTitle?: string }) =>
      api.home.patchSettings(classroomId, patch),
    onSuccess: (next) => {
      queryClient.setQueryData<ClassroomSettingsDto>(['classroom-settings', classroomId], next);
    },
  });

  // What's currently persisted (post-blur) — drives the "Open PDF"
  // verify link, so it always points at the saved URL rather than the
  // half-typed one in the input. Trim before doing the safety check —
  // a sea of whitespace shouldn't render as a clickable link.
  const savedUrl = settings?.schoolCalUrl?.trim() ?? '';
  const isHttpUrl = /^https?:\/\//i.test(savedUrl);
  const savedTitle = settings?.schoolCalTitle?.trim() ?? '';

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>School calendar</h3>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="cal-url">
          URL
        </label>
        <input
          id="cal-url"
          className={styles.input}
          type="url"
          value={url}
          placeholder="https://example.com/calendar.pdf"
          maxLength={500}
          disabled={isLoading}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => {
            if (settings && url !== settings.schoolCalUrl) save.mutate({ schoolCalUrl: url });
          }}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="cal-title">
          Title
        </label>
        <input
          id="cal-title"
          className={styles.input}
          type="text"
          value={title}
          placeholder="e.g. 2025–26 school calendar"
          maxLength={120}
          disabled={isLoading}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => {
            if (settings && title !== settings.schoolCalTitle)
              save.mutate({ schoolCalTitle: title });
          }}
        />
      </div>

      {/* "Load it" — once both fields are saved the teacher gets a
       * verify link here AND the title shows as a pill on the live
       * Calendar header so kids can open it during morning meeting. */}
      {isHttpUrl ? (
        <a
          href={savedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.calLink}
          data-testid="school-cal-open"
        >
          <span aria-hidden="true">📄</span>
          <span>Open “{savedTitle || 'School calendar'}” in a new tab</span>
        </a>
      ) : (
        <span className={styles.hint}>
          Once the URL starts with <code>https://</code> the calendar will appear on the live
          Calendar page as a tappable pill.
        </span>
      )}
    </div>
  );
}

function YesterdayTomorrowToggle({
  preferences,
  isLoading,
}: {
  preferences: PreferencesDto | undefined;
  isLoading: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (next: boolean) => api.preferences.update({ showYesterdayTomorrow: next }),
    onSuccess: (next) => {
      queryClient.setQueryData<PreferencesDto>(['preferences'], next);
    },
  });

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>K–2 helpers</h3>
      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={preferences?.showYesterdayTomorrow ?? false}
          disabled={isLoading}
          onChange={(e) => save.mutate(e.target.checked)}
        />
        Show &ldquo;Yesterday was…&rdquo; / &ldquo;Tomorrow is…&rdquo;
      </label>
      <span className={styles.hint}>Applies to all classrooms.</span>
    </div>
  );
}

function EventsSection({
  classroomId,
  events,
  isLoading,
}: {
  classroomId: string;
  events: CalendarEventDto[];
  isLoading: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');

  const create = useMutation({
    mutationFn: (body: { eventDate: string; title: string }) =>
      api.calendar.createEvent(classroomId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-events', classroomId] });
      setDate('');
      setTitle('');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.calendar.removeEvent(classroomId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-events', classroomId] });
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!date || !title.trim()) return;
    create.mutate({ eventDate: date, title: title.trim() });
  };

  const sorted = [...events].sort((a, b) => a.eventDate.localeCompare(b.eventDate));

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Class events</h3>

      {/* Add form lives at the TOP so the most common action stays at
       * eye level. The list below scrolls vertically rather than
       * paginating — the right-to-left page nav read as awkward when
       * teachers added 5+ events. */}
      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="event-date">
            Date
          </label>
          <input
            id="event-date"
            className={styles.input}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="event-title">
            Title
          </label>
          <input
            id="event-title"
            className={styles.input}
            type="text"
            value={title}
            placeholder="e.g. Picture day"
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className={`${styles.button} ${styles.buttonPrimary}`}
          disabled={!date || !title.trim() || create.isPending}
        >
          +
        </button>
      </form>

      {isLoading ? (
        <p className={styles.empty}>Loading…</p>
      ) : sorted.length === 0 ? (
        <p className={styles.empty}>No events yet.</p>
      ) : (
        <ul className={`${styles.list} ${styles.listScroll}`}>
          {sorted.map((ev) => (
            <li key={ev.id} className={styles.listItem}>
              <div className={styles.listItemMain}>
                <span className={styles.listItemTitle}>{ev.title}</span>
                <span className={styles.listItemMeta}>{formatDate(ev.eventDate)}</span>
              </div>
              <button
                type="button"
                className={styles.buttonGhost}
                disabled={remove.isPending}
                onClick={() => remove.mutate(ev.id)}
                aria-label={`Remove ${ev.title}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BirthdaysSection({
  classroomId,
  birthdays,
  isLoading,
}: {
  classroomId: string;
  birthdays: BirthdayDto[];
  isLoading: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [month, setMonth] = useState<number>(1);
  const [day, setDay] = useState<number>(1);

  const create = useMutation({
    mutationFn: (body: { name: string; month: number; day: number }) =>
      api.calendar.createBirthday(classroomId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-birthdays', classroomId] });
      setName('');
    },
  });

  const clearAll = useMutation({
    mutationFn: () => api.calendar.clearBirthdays(classroomId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-birthdays', classroomId] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.calendar.removeBirthday(classroomId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-birthdays', classroomId] });
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate({ name: trimmed, month, day });
  };

  // Sorted by month, then day, then name so duplicates land together
  // and the list reads like a roster.
  const sorted = [...birthdays].sort(
    (a, b) => a.month - b.month || a.day - b.day || a.name.localeCompare(b.name),
  );
  const count = sorted.length;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Student birthdays</h3>

      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="bday-name">
            Name
          </label>
          <input
            id="bday-name"
            className={styles.input}
            type="text"
            value={name}
            placeholder="e.g. Avery"
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="bday-month">
            Mo
          </label>
          <select
            id="bday-month"
            className={styles.input}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="bday-day">
            Day
          </label>
          <input
            id="bday-day"
            className={styles.input}
            type="number"
            min={1}
            max={31}
            value={day}
            onChange={(e) => setDay(Math.max(1, Math.min(31, Number(e.target.value) || 1)))}
          />
        </div>
        <button
          type="submit"
          className={`${styles.button} ${styles.buttonPrimary}`}
          disabled={!name.trim() || create.isPending}
        >
          +
        </button>
      </form>

      {isLoading ? (
        <p className={styles.empty}>Loading…</p>
      ) : count === 0 ? (
        <p className={styles.empty}>No birthdays added.</p>
      ) : (
        <ul className={`${styles.list} ${styles.listScroll}`} data-testid="birthdays-loaded">
          {sorted.map((b) => (
            <li key={b.id} className={styles.listItem}>
              <div className={styles.listItemMain}>
                <span className={styles.listItemTitle}>{b.name}</span>
                <span className={styles.listItemMeta}>
                  {MONTHS[b.month - 1]} {b.day}
                </span>
              </div>
              <button
                type="button"
                className={styles.buttonGhost}
                disabled={remove.isPending}
                onClick={() => remove.mutate(b.id)}
                aria-label={`Remove ${b.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <BirthdayImport classroomId={classroomId} />

      {count > 0 ? (
        <button
          type="button"
          className={styles.buttonGhost}
          disabled={clearAll.isPending}
          onClick={() => {
            if (window.confirm(`Remove all ${count} birthdays? This cannot be undone.`)) {
              clearAll.mutate();
            }
          }}
        >
          {clearAll.isPending ? 'Clearing…' : `Clear all (${count})`}
        </button>
      ) : null}
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
